import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError, type QuotaRequestMessageRecord, type QuotaRequestRecord } from "../api";
import { useAuth } from "../context/AuthContext";
import { reportRuntimeError } from "../reportRuntimeError";
import { throwIfFatalError, useFatalErrorState } from "../useFatalErrorState";
import {
  ASSIGNED_WORKFLOW_STATUS_OPTIONS,
  awsStatusExpectsSupportCaseId,
  buildSortedProjectOptions,
  buildSupportCaseConsoleUrl,
  formatProjectOptionChoiceLabel,
  formatQuotaRequestAccountRegionHeaderLine,
  formatRequestedAtTimestamp,
  PROJECT_DISPLAY_UNASSIGNED_LABEL,
  PROJECT_SELECT_NEW_PROJECT_LABEL,
  PROJECT_SELECT_NEW_PROJECT_VALUE,
  PROJECT_SELECT_UNASSIGNED_VALUE,
  type ProjectOption,
  QUOTA_REQUEST_DEFAULT_PRIORITY,
  QUOTA_REQUEST_PRIORITY_OPTIONS,
  resolveProjectSelectValue,
  resolveQuotaNameDisplayForRow,
  shouldShowNewProjectOption,
  shouldShowUnassignedProjectOption,
  SUPPORT_CASE_MISSING_LABEL,
  type QuarantineQuotaRequestRow,
  WORKFLOW_STATUS_MUST_RESPOND,
  WORKFLOW_STATUS_QUARANTINE,
  WORKFLOW_STATUS_TRACKED,
} from "./quarantineQuotaRequestTable";

function sortMessagesNewestFirst(
  messages: QuotaRequestMessageRecord[],
): QuotaRequestMessageRecord[] {
  return [...messages].sort(
    (left, right) => right.created_at_timestamp - left.created_at_timestamp,
  );
}

function buildQuotaRequestMessageListUrl(quotaRequestRowId: string, sortId: string | null): string {
  const path = `/quota_request/${quotaRequestRowId}/message/list`;

  if (sortId === null || sortId === "") {
    return path;
  }

  return `${path}?sort_id=${encodeURIComponent(sortId)}`;
}

export function QuotaRequestMessageListPage() {
  const { quotaRequestId } = useParams();
  const [searchParams] = useSearchParams();
  const listingSortId = searchParams.get("sort_id");
  const { token, user, logout } = useAuth();
  const [messages, setMessages] = useState<QuotaRequestMessageRecord[]>([]);
  const [quotaRequestCreatedAtTimestamp, setQuotaRequestCreatedAtTimestamp] = useState<
    number | undefined
  >(undefined);
  const [listingQuotaRequestIds, setListingQuotaRequestIds] = useState<string[] | null>(null);
  const [quotaRequestRow, setQuotaRequestRow] = useState<QuotaRequestRecord | null>(null);
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [operatorFieldErrorMessage, setOperatorFieldErrorMessage] = useState<string | null>(null);
  const [draftComment, setDraftComment] = useState("");
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  const [fatalError, failWithError] = useFatalErrorState();
  const [isDetailLoading, setIsDetailLoading] = useState(true);
  const [isMessagesLoading, setIsMessagesLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sortedMessages = useMemo(() => sortMessagesNewestFirst(messages), [messages]);
  const isAddEnabled = draftComment.trim() !== "" && !isSubmitting;
  const supportCaseConsoleUrl = useMemo(
    () => buildSupportCaseConsoleUrl(quotaRequestRow),
    [quotaRequestRow],
  );
  const awsSupportCaseId = quotaRequestRow?.aws_support_case_id ?? "";
  const hasLinkedSupportCase = awsSupportCaseId !== "";
  const showMissingSupportCaseBanner =
    quotaRequestRow !== null
    && awsStatusExpectsSupportCaseId(quotaRequestRow.aws_status ?? "")
    && awsSupportCaseId === "";
  const quotaRequestAccountRegionHeaderLine = formatQuotaRequestAccountRegionHeaderLine(
    quotaRequestRow?.aws_account_id,
    quotaRequestRow?.deployment_environment_name,
    quotaRequestRow?.aws_region,
  );

  const sortedProjectOptions = useMemo(
    () => buildSortedProjectOptions(projectOptions),
    [projectOptions],
  );

  const listingNavigation = useMemo(() => {
    if (quotaRequestId === undefined || listingQuotaRequestIds === null) {
      return null;
    }

    const totalCount = listingQuotaRequestIds.length;
    const currentIndex = listingQuotaRequestIds.indexOf(quotaRequestId);

    if (currentIndex < 0 || totalCount === 0) {
      return {
        currentIndex,
        totalCount,
        previousQuotaRequestId: null as string | null,
        nextQuotaRequestId: null as string | null,
        showPosition: false,
      };
    }

    const previousQuotaRequestId =
      currentIndex > 0 ? listingQuotaRequestIds[currentIndex - 1] : null;
    const nextQuotaRequestId =
      currentIndex < totalCount - 1 ? listingQuotaRequestIds[currentIndex + 1] : null;

    return {
      currentIndex,
      totalCount,
      previousQuotaRequestId,
      nextQuotaRequestId,
      showPosition: true,
    };
  }, [quotaRequestId, listingQuotaRequestIds]);

  useEffect(() => {
    let isCancelled = false;

    async function loadQuotaRequestDetailAndListing() {
      if (!token || quotaRequestId === undefined) {
        return;
      }

      setIsDetailLoading(true);

      try {
        const [loadedQuotaRequestRow, loadedProjectList] = await Promise.all([
          api.getQuotaRequest(token, quotaRequestId),
          api.listProjects(token),
        ]);

        if (isCancelled) {
          return;
        }

        setQuotaRequestRow(loadedQuotaRequestRow);
        setQuotaRequestCreatedAtTimestamp(loadedQuotaRequestRow.created_at_timestamp);
        setProjectOptions(
          loadedProjectList.map((projectRecord) => ({
            id: projectRecord.id,
            name: projectRecord.name,
            created_at_timestamp: projectRecord.created_at_timestamp,
          })),
        );

        if (listingSortId === null || listingSortId === "") {
          setListingQuotaRequestIds(null);

          return;
        }

        const listingBody = await api.getQuotaRequestListing(token, listingSortId);

        if (!isCancelled) {
          setListingQuotaRequestIds(listingBody.quota_request_ids);
        }
      } catch (loadDetailError) {
        if (!isCancelled) {
          failWithError(loadDetailError);
        }
      } finally {
        if (!isCancelled) {
          setIsDetailLoading(false);
        }
      }
    }

    loadQuotaRequestDetailAndListing();

    return () => {
      isCancelled = true;
    };
  }, [token, quotaRequestId, listingSortId, failWithError]);

  useEffect(() => {
    let isCancelled = false;

    async function loadOperatorMessages() {
      if (!token || quotaRequestId === undefined) {
        return;
      }

      setIsMessagesLoading(true);

      try {
        const responseBody = await api.listQuotaRequestMessages(token, quotaRequestId);

        if (!isCancelled) {
          setMessages(responseBody);
        }
      } catch (loadError) {
        if (!isCancelled) {
          failWithError(loadError);
        }
      } finally {
        if (!isCancelled) {
          setIsMessagesLoading(false);
        }
      }
    }

    loadOperatorMessages();

    return () => {
      isCancelled = true;
    };
  }, [token, quotaRequestId, failWithError]);

  const isPageLoading = isDetailLoading || isMessagesLoading;

  throwIfFatalError(fatalError);

  function applyOperatorPatchToQuotaRequestRow(updatedRow: Record<string, unknown>) {
    setQuotaRequestRow((currentRow) => {
      if (currentRow === null) {
        return currentRow;
      }

      return {
        ...currentRow,
        ...updatedRow,
      } as QuotaRequestRecord;
    });
  }

  async function handleProjectSelectionChange(nextSelectValue: string) {
    if (!token || quotaRequestRow === null || quotaRequestId === undefined) {
      return;
    }

    const operatorRow = quotaRequestRow as QuarantineQuotaRequestRow;
    const previousSelectValue = resolveProjectSelectValue(operatorRow);

    if (nextSelectValue === previousSelectValue) {
      return;
    }

    if (nextSelectValue === PROJECT_SELECT_NEW_PROJECT_VALUE) {
      const projectName = window.prompt("Enter a name for the new project:");

      if (projectName === null || projectName.trim() === "") {
        return;
      }

      try {
        const createdProject = await api.createProject(token, projectName.trim());
        const updatedRow = await api.patchQuotaRequest(token, quotaRequestId, {
          project_id: createdProject.id,
        });

        setProjectOptions((currentProjectOptions) => {
          const existingProject = currentProjectOptions.find(
            (projectOption) => projectOption.id === createdProject.id,
          );

          if (existingProject !== undefined) {
            return currentProjectOptions;
          }

          return [
            ...currentProjectOptions,
            {
              id: createdProject.id,
              name: createdProject.name,
              created_at_timestamp: createdProject.created_at_timestamp,
            },
          ];
        });
        applyOperatorPatchToQuotaRequestRow(updatedRow);
        setOperatorFieldErrorMessage(null);
      } catch (patchError) {
        reportRuntimeError(patchError);
        const message =
          patchError instanceof ApiError || patchError instanceof Error
            ? patchError.message
            : "unknown error";
        setOperatorFieldErrorMessage(message);
      }

      return;
    }

    const workflowStatus = operatorRow.status ?? WORKFLOW_STATUS_QUARANTINE;

    if (
      nextSelectValue === PROJECT_SELECT_UNASSIGNED_VALUE
      && workflowStatus === WORKFLOW_STATUS_TRACKED
    ) {
      const confirmed = window.confirm(
        "Change this quota request back to quarantine and remove its project assignment?",
      );

      if (!confirmed) {
        return;
      }

      try {
        const updatedRow = await api.patchQuotaRequest(token, quotaRequestId, {
          project_id: null,
        });

        applyOperatorPatchToQuotaRequestRow(updatedRow);
        setOperatorFieldErrorMessage(null);
      } catch (patchError) {
        reportRuntimeError(patchError);
        const message =
          patchError instanceof ApiError || patchError instanceof Error
            ? patchError.message
            : "unknown error";
        setOperatorFieldErrorMessage(message);
      }

      return;
    }

    if (nextSelectValue === PROJECT_SELECT_UNASSIGNED_VALUE) {
      return;
    }

    try {
      const updatedRow = await api.patchQuotaRequest(token, quotaRequestId, {
        project_id: nextSelectValue,
      });

      applyOperatorPatchToQuotaRequestRow(updatedRow);
      setOperatorFieldErrorMessage(null);
    } catch (patchError) {
      reportRuntimeError(patchError);
      const message =
        patchError instanceof ApiError || patchError instanceof Error
          ? patchError.message
          : "unknown error";
      setOperatorFieldErrorMessage(message);
    }
  }

  async function handleWorkflowStatusChange(nextStatus: string) {
    if (!token || quotaRequestRow === null || quotaRequestId === undefined) {
      return;
    }

    const workflowStatus = quotaRequestRow.status ?? WORKFLOW_STATUS_QUARANTINE;

    if (nextStatus === workflowStatus) {
      return;
    }

    try {
      const updatedRow = await api.patchQuotaRequest(token, quotaRequestId, {
        status: nextStatus,
      });

      applyOperatorPatchToQuotaRequestRow(updatedRow);
      setOperatorFieldErrorMessage(null);
    } catch (patchError) {
      reportRuntimeError(patchError);
      const message =
        patchError instanceof ApiError || patchError instanceof Error
          ? patchError.message
          : "unknown error";
      setOperatorFieldErrorMessage(message);
    }
  }

  async function handlePriorityChange(nextPriority: string) {
    if (!token || quotaRequestRow === null || quotaRequestId === undefined) {
      return;
    }

    const currentPriority = quotaRequestRow.priority ?? QUOTA_REQUEST_DEFAULT_PRIORITY;

    if (nextPriority === currentPriority) {
      return;
    }

    try {
      const updatedRow = await api.patchQuotaRequest(token, quotaRequestId, {
        priority: nextPriority,
      });

      applyOperatorPatchToQuotaRequestRow(updatedRow);
      setOperatorFieldErrorMessage(null);
    } catch (patchError) {
      reportRuntimeError(patchError);
      const message =
        patchError instanceof ApiError || patchError instanceof Error
          ? patchError.message
          : "unknown error";
      setOperatorFieldErrorMessage(message);
    }
  }

  async function handleAddComment() {
    if (!token || quotaRequestId === undefined || draftComment.trim() === "") {
      return;
    }

    setIsSubmitting(true);
    setSubmitErrorMessage(null);

    try {
      const createdMessage = await api.createQuotaRequestMessage(
        token,
        quotaRequestId,
        draftComment,
      );
      setDraftComment("");
      setMessages((currentMessages) => [createdMessage, ...currentMessages]);
    } catch (submitError) {
      reportRuntimeError(submitError);
      const message = submitError instanceof Error ? submitError.message : "unknown error";
      setSubmitErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isPageLoading || quotaRequestRow === null) {
    return (
      <main style={{ fontFamily: "system-ui, sans-serif", margin: "2rem" }}>
        <p>Loading…</p>
      </main>
    );
  }

  const workflowStatus = quotaRequestRow.status ?? WORKFLOW_STATUS_QUARANTINE;
  const operatorQuotaRequestRow = quotaRequestRow as QuarantineQuotaRequestRow;
  const projectSelectValue = resolveProjectSelectValue(operatorQuotaRequestRow);
  const prioritySelectValue = quotaRequestRow.priority ?? QUOTA_REQUEST_DEFAULT_PRIORITY;
  const quotaRequestTitleLine = resolveQuotaNameDisplayForRow(operatorQuotaRequestRow);
  const supportCaseDisplayId = awsSupportCaseId;

  return (
    <main style={{ fontFamily: "system-ui, sans-serif", margin: "2rem" }}>
      <header style={{ width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: "1.25rem",
          }}
        >
          <p style={{ margin: 0 }}>
            <Link to="/">Back to quarantine list</Link>
          </p>
          <div>
            {user ? <span style={{ marginRight: "1rem" }}>{user.email}</span> : null}
            <button type="button" onClick={logout}>Sign out</button>
          </div>
        </div>
        <div
          style={{
            boxSizing: "border-box",
            width: "100%",
            borderTop: "1px solid #ccc",
            borderBottom: "1px solid #ccc",
            padding: "0.75rem 0",
            marginBottom: "0.75rem",
          }}
        >
            <p
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.5rem 1rem",
                margin: 0,
              }}
            >
              <span
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <span>ID: {quotaRequestId ?? "—"}</span>
                <span style={{ color: "#aaa", margin: "0 0.75rem" }} aria-hidden="true">|</span>
                <span>
                  {formatRequestedAtTimestamp(quotaRequestCreatedAtTimestamp) || "—"}
                </span>
                <span style={{ color: "#aaa", margin: "0 0.75rem" }} aria-hidden="true">|</span>
                <span style={{ fontWeight: 700 }}>{quotaRequestAccountRegionHeaderLine}</span>
                <span style={{ color: "#aaa", margin: "0 0.75rem" }} aria-hidden="true">|</span>
                <select
                  aria-label={`Project for quota request ${quotaRequestId}`}
                  value={projectSelectValue}
                  onChange={(changeEvent) => {
                    void handleProjectSelectionChange(changeEvent.target.value);
                  }}
                  style={{ maxWidth: "14rem" }}
                >
                  {shouldShowUnassignedProjectOption(workflowStatus) ? (
                    <option value={PROJECT_SELECT_UNASSIGNED_VALUE}>
                      {PROJECT_DISPLAY_UNASSIGNED_LABEL}
                    </option>
                  ) : null}
                  {sortedProjectOptions.map((projectOption) => (
                    <option key={projectOption.id} value={projectOption.id}>
                      {formatProjectOptionChoiceLabel(projectOption)}
                    </option>
                  ))}
                  {shouldShowNewProjectOption(workflowStatus) ? (
                    <option value={PROJECT_SELECT_NEW_PROJECT_VALUE}>
                      {PROJECT_SELECT_NEW_PROJECT_LABEL}
                    </option>
                  ) : null}
                </select>
                <span style={{ color: "#aaa", margin: "0 0.75rem" }} aria-hidden="true">|</span>
                {workflowStatus === WORKFLOW_STATUS_QUARANTINE
                  || projectSelectValue === PROJECT_SELECT_UNASSIGNED_VALUE ? (
                    <span>{WORKFLOW_STATUS_QUARANTINE}</span>
                  ) : (
                    <select
                      aria-label={`Status for quota request ${quotaRequestId}`}
                      value={workflowStatus}
                      onChange={(changeEvent) => {
                        void handleWorkflowStatusChange(changeEvent.target.value);
                      }}
                    >
                      {ASSIGNED_WORKFLOW_STATUS_OPTIONS.map((statusOption) => (
                        <option key={statusOption} value={statusOption}>
                          {statusOption === WORKFLOW_STATUS_MUST_RESPOND
                            ? "must respond"
                            : statusOption}
                        </option>
                      ))}
                    </select>
                  )}
                <span style={{ color: "#aaa", margin: "0 0.75rem" }} aria-hidden="true">|</span>
                <select
                  aria-label={`Priority for quota request ${quotaRequestId}`}
                  value={prioritySelectValue}
                  onChange={(changeEvent) => {
                    void handlePriorityChange(changeEvent.target.value);
                  }}
                >
                  {QUOTA_REQUEST_PRIORITY_OPTIONS.map((priorityOption) => (
                    <option key={priorityOption} value={priorityOption}>
                      {priorityOption}
                    </option>
                  ))}
                </select>
              </span>
              {listingNavigation !== null ? (
                <span
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginLeft: "auto",
                  }}
                >
                  {listingNavigation.showPosition === true ? (
                    <>
                      <span>
                        Request {listingNavigation.currentIndex + 1} of {listingNavigation.totalCount}
                      </span>
                      <span style={{ color: "#aaa", margin: "0 0.75rem" }} aria-hidden="true">|</span>
                    </>
                  ) : null}
                  {listingNavigation.previousQuotaRequestId !== null ? (
                    <Link
                      to={buildQuotaRequestMessageListUrl(
                        listingNavigation.previousQuotaRequestId,
                        listingSortId,
                      )}
                    >
                      Back
                    </Link>
                  ) : (
                    <span style={{ color: "#888" }}>Back</span>
                  )}
                  <span style={{ color: "#aaa", margin: "0 0.75rem" }} aria-hidden="true">|</span>
                  {listingNavigation.nextQuotaRequestId !== null ? (
                    <Link
                      to={buildQuotaRequestMessageListUrl(
                        listingNavigation.nextQuotaRequestId,
                        listingSortId,
                      )}
                    >
                      Next
                    </Link>
                  ) : (
                    <span style={{ color: "#888" }}>Next</span>
                  )}
                </span>
              ) : null}
            </p>
            {operatorFieldErrorMessage !== null ? (
              <p style={{ color: "#b00020", margin: "0.5rem 0 0" }}>{operatorFieldErrorMessage}</p>
            ) : null}
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "0.75rem 1.5rem",
            marginBottom: "1.25rem",
          }}
        >
          <h1 style={{ margin: 0, fontSize: "1.5rem", flex: "1 1 16rem" }}>
            {JSON.stringify(quotaRequestTitleLine)}
          </h1>
          <div style={{ flex: "0 1 auto", textAlign: "right", maxWidth: "100%" }}>
            {showMissingSupportCaseBanner ? (
              <p
                role="alert"
                style={{
                  color: "#b00020",
                  fontWeight: 600,
                  border: "1px solid #b00020",
                  padding: "0.5rem 0.75rem",
                  margin: 0,
                  textAlign: "left",
                }}
              >
                {SUPPORT_CASE_MISSING_LABEL} (AWS status: {quotaRequestRow.aws_status ?? "—"}).
                Re-download quota requests after AWS links a support case to this Service Quotas
                request.
              </p>
            ) : hasLinkedSupportCase ? (
              <p style={{ margin: 0 }}>
                Support case{" "}
                {supportCaseConsoleUrl !== null ? (
                  <a href={supportCaseConsoleUrl} target="_blank" rel="noopener noreferrer">
                    {supportCaseDisplayId}
                  </a>
                ) : (
                  <span>{supportCaseDisplayId}</span>
                )}
              </p>
            ) : (
              <p style={{ margin: 0 }}>No AWS support case is linked to this quota request yet.</p>
            )}
          </div>
        </div>
        <h2 style={{ marginTop: 0, fontSize: "1.25rem", fontWeight: 600 }}>Message history</h2>
      </header>

      <section style={{ marginBottom: "2rem" }}>
        <h2 style={{ fontSize: "1.1rem", borderBottom: "1px solid #ccc", paddingBottom: "0.5rem" }}>
          Our messages
        </h2>

        <label htmlFor="quota-request-new-comment" style={{ display: "block", marginBottom: "0.5rem" }}>
          New message
        </label>
        <textarea
          id="quota-request-new-comment"
          value={draftComment}
          onChange={(changeEvent) => {
            setDraftComment(changeEvent.target.value);
          }}
          rows={4}
          style={{ width: "100%", maxWidth: "40rem", display: "block", marginBottom: "0.5rem" }}
        />
        <button type="button" disabled={!isAddEnabled} onClick={handleAddComment}>
          Add
        </button>

        {submitErrorMessage !== null ? <p role="alert">Error: {submitErrorMessage}</p> : null}

        <div style={{ marginTop: "1rem" }}>
          {sortedMessages.length === 0 ? <p>No messages yet.</p> : null}
          <ul style={{ listStyle: "none", padding: 0, maxWidth: "40rem" }}>
            {sortedMessages.map((message) => (
              <li
                key={message.id}
                style={{
                  borderBottom: "1px solid #ddd",
                  padding: "0.75rem 0",
                }}
              >
                <div style={{ color: "#555", fontSize: "0.9rem", marginBottom: "0.35rem" }}>
                  {formatRequestedAtTimestamp(message.created_at_timestamp)}
                </div>
                <div style={{ whiteSpace: "pre-wrap" }}>{message.comment}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
}
